/*
 * stories-campaign.js
 * ------------------------------------------------------------------
 * Instagram / TikTok style "Stories" experience for the
 * `sections/stories-campaign.liquid` section.
 *
 * Hand-written vanilla ES6. No build step, no framework, no npm.
 * Pattern: IIFE + class, booted on DOMContentLoaded AND on
 * `shopify:section:load` (theme editor). Globals guarded, listeners
 * cleaned up on re-init / teardown.
 *
 * ==================================================================
 * CSS CLASS NAMES INTRODUCED BY THIS SCRIPT (for the CSS developer)
 * All live under the existing `stories__` namespace. The section's
 * scoped `#stories-{id} .stories__x` rules (specificity 1,1,0) will
 * override the low-specificity functional defaults this file injects.
 *
 *  Player chrome (built inside [data-stories-player]):
 *    .stories__progress            top segment bar container   [data-progress]
 *    .stories__progress-seg        one segment (per story)
 *      .is-filled / .is-active     state on a segment
 *    .stories__progress-fill       animating inner fill of a segment
 *    .stories__rail                right-side control rail      [data-rail]
 *    .stories__rail-btn            generic rail button
 *    .stories__more                "More" (···) button         [data-rail-more]
 *    .stories__like                like button                 [data-rail-like]
 *      .is-liked                   state when liked
 *    .stories__like-icon           heart glyph
 *      .is-bump                    transient like animation hook
 *    .stories__like-count          formatted like count
 *    .stories__sound               sound / mute button         [data-rail-sound]
 *      .is-muted / .is-disabled    state
 *    .stories__sound-icon          speaker glyph
 *
 *  Slides (built inside [data-player-track]; track also gets .stories__slides):
 *    .stories__slide              one full-screen story        [data-index][data-story-id][data-story-type]
 *      .stories__slide--text / --photo / --video   type modifier
 *      .is-active                 currently visible slide
 *    .stories__slide-media        media layer
 *    .stories__slide-img          <img> for photo stories
 *    .stories__slide-video        <video> for video stories
 *    .stories__slide-scrim        readability gradient overlay
 *    .stories__slide-body         bottom text block
 *    .stories__slide-badge        category badge (uses --cat-color / --cat-text)
 *    .stories__slide-quote        the quote
 *    .stories__slide-meta         "author · context"
 *    .stories__slide-excerpt      first lines of the body (teaser)
 *    .stories__slide-readmore     opens the full story in the read modal
 *
 *  State classes toggled on EXISTING markup:
 *    [data-stories-player].is-open / [data-topic-sheet].is-open
 *    [data-sheet-pill].is-selected      multi-select filter state (+ inline bg)
 *    [data-sheet-tab].is-active         active sheet tab
 *    [data-filter-pill].is-active       active desktop grid filter (existing convention)
 *    body.stories-body-lock             scroll lock while player/sheet open
 *
 *  data-* ADDED by JS (on generated slides): data-index, data-story-id,
 *    data-story-type. (All others are read from the server markup.)
 * ==================================================================
 */
(function () {
  'use strict';

  var MOBILE_BP = 1024;
  var RUNTIME_STYLE_ID = 'stories-campaign-runtime-css';

  /* ---------------------------------------------------------------- utils */

  function device() {
    return window.innerWidth < MOBILE_BP ? 'mobile' : 'desktop';
  }

  function prefersReducedMotion() {
    try {
      return window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    } catch (e) {
      return false;
    }
  }

  // Pull a URL into the HTTP cache without creating a live <img> in the document.
  // Used for the outer ring of the media window: by the time a fast swiper gets
  // there the bytes are already local, so attaching src paints on the next frame
  // instead of showing a black slide. Deduped — a story can appear twice after a
  // filter change / feed reload.
  var _warmed = {};
  function warmCache(url) {
    if (!url || _warmed[url]) return;
    _warmed[url] = true;
    try {
      var im = new Image();
      im.decoding = 'async';
      im.src = url;
    } catch (e) { /* ignore */ }
  }

  // GA4 helper — pushes exactly one event object onto the existing dataLayer.
  function pushDL(event, params) {
    try {
      window.dataLayer = window.dataLayer || [];
      var payload = { event: event };
      if (params) {
        for (var k in params) {
          if (Object.prototype.hasOwnProperty.call(params, k)) payload[k] = params[k];
        }
      }
      window.dataLayer.push(payload);
    } catch (e) {
      /* never let analytics break the UI */
    }
  }

  // localStorage wrappers (Safari private mode / disabled storage safe).
  function lsGet(key) {
    try { return window.localStorage.getItem(key); } catch (e) { return null; }
  }
  function lsSet(key, val) {
    try { window.localStorage.setItem(key, val); } catch (e) { /* ignore */ }
  }

  // 5100 -> "5.1k", 12000 -> "12k", 999 -> "999", 1200000 -> "1.2m"
  function formatCount(n) {
    n = Number(n) || 0;
    var abs = Math.abs(n);
    if (abs < 1000) return String(n);
    if (abs < 1000000) {
      var k = n / 1000;
      var kv = abs < 100000 ? Math.round(k * 10) / 10 : Math.round(k);
      return String(kv).replace(/\.0$/, '') + 'k';
    }
    var m = Math.round((n / 1000000) * 10) / 10;
    return String(m).replace(/\.0$/, '') + 'm';
  }

  function toInt(v, fallback) {
    var n = parseInt(v, 10);
    return isNaN(n) ? fallback : n;
  }
  function toFloat(v, fallback) {
    var n = parseFloat(v);
    return isNaN(n) ? fallback : n;
  }
  function clamp(v, min, max) {
    return v < min ? min : (v > max ? max : v);
  }

  function debounce(fn, wait) {
    var t;
    return function () {
      var ctx = this, args = arguments;
      clearTimeout(t);
      t = setTimeout(function () { fn.apply(ctx, args); }, wait);
    };
  }

  function safePlay(video) {
    if (!video) return;
    try {
      var p = video.play();
      if (p && typeof p.catch === 'function') {
        p.catch(function () {
          // Autoplay policies reject UNMUTED playback outside a user gesture, so
          // once the viewer turns sound on every later slide would silently stay
          // paused. Fall back to muted playback — a playing story beats silence.
          if (!video.muted) {
            video.muted = true;
            try {
              var p2 = video.play();
              if (p2 && typeof p2.catch === 'function') p2.catch(function () {});
            } catch (e2) { /* ignore */ }
          }
        });
      }
    } catch (e) { /* ignore */ }
  }

  // Snap a media element (img or video) to 16:9 / 9:16 by tagging is-landscape /
  // is-portrait on it and its host. Uses the server-provided orientation when
  // known, else measures natural/intrinsic size once it loads.
  function applyMediaOrientation(media, host, known) {
    function set(landscape) {
      media.classList.toggle('is-landscape', landscape);
      media.classList.toggle('is-portrait', !landscape);
      if (host) {
        host.classList.toggle('is-landscape', landscape);
        host.classList.toggle('is-portrait', !landscape);
      }
    }
    if (known === 'landscape') { set(true); return; }
    if (known === 'portrait') { set(false); return; }
    var isVideo = media.tagName === 'VIDEO';
    function detect() {
      var w = isVideo ? media.videoWidth : media.naturalWidth;
      var h = isVideo ? media.videoHeight : media.naturalHeight;
      if (!w || !h) return;
      set(w >= h);
    }
    if (isVideo) {
      if (media.readyState >= 1) detect();
      else media.addEventListener('loadedmetadata', detect, { once: true });
    } else {
      if (media.complete && media.naturalWidth) detect();
      else media.addEventListener('load', detect, { once: true });
    }
  }

  /* -------------------------------------------------- body scroll locking */
  // Shared across all instances/layers so nested open states don't fight.
  var _lockCount = 0;
  var _prevOverflow = '';
  function lockScroll() {
    if (_lockCount === 0) {
      _prevOverflow = document.body.style.overflow;
      document.body.style.overflow = 'hidden';
      document.body.classList.add('stories-body-lock');
    }
    _lockCount++;
  }
  function unlockScroll() {
    _lockCount = Math.max(0, _lockCount - 1);
    if (_lockCount === 0) {
      document.body.style.overflow = _prevOverflow;
      document.body.classList.remove('stories-body-lock');
    }
  }

  /* ---------------------------------------- functional runtime stylesheet */
  // Structural-only, low specificity. Guarantees the player scrolls/snaps
  // and progress animates out of the box; the section's scoped CSS wins.
  function injectRuntimeCSS() {
    if (document.getElementById(RUNTIME_STYLE_ID)) return;
    var css = [
      '.stories__player{position:fixed;inset:0;z-index:60;background:#000;color:#fff;}',
      '.stories__player[hidden]{display:none;}',
      '.stories__slides{position:absolute;inset:0;overflow-y:auto;overflow-x:hidden;scroll-snap-type:y mandatory;-webkit-overflow-scrolling:touch;scrollbar-width:none;}',
      '.stories__slides::-webkit-scrollbar{display:none;}',
      '.stories__slide{position:relative;width:100%;height:100%;scroll-snap-align:start;scroll-snap-stop:always;overflow:hidden;display:flex;flex-direction:column;justify-content:flex-end;}',
      '.stories__slide--text{background:linear-gradient(160deg,#0e3b2c,#0a2119);}',
      '.stories__slide-media{position:absolute;inset:0;z-index:0;}',
      '.stories__slide-img,.stories__slide-video{position:absolute;inset:0;width:100%;height:100%;object-fit:cover;}',
      '.stories__slide-scrim{position:absolute;inset:0;z-index:1;pointer-events:none;background:linear-gradient(180deg,rgba(0,0,0,.35) 0%,rgba(0,0,0,0) 30%,rgba(0,0,0,0) 55%,rgba(0,0,0,.8) 100%);}',
      '.stories__slide-body{position:relative;z-index:2;padding:24px 84px 40px 24px;}',
      '.stories__slide-badge{display:inline-block;font-size:11px;font-weight:700;letter-spacing:.05em;text-transform:uppercase;padding:5px 10px;border-radius:6px;margin-bottom:12px;background:var(--cat-color,#00d282);color:var(--cat-text,#04181e);}',
      '.stories__slide-quote{font-size:22px;line-height:1.25;font-weight:800;margin:0 0 10px;}',
      '.stories__slide-meta{font-size:14px;opacity:.85;margin:0;}',
      '.stories__slide-excerpt{margin:10px 0 0;font-size:15px;line-height:1.5;opacity:.92;}',
      '.stories__slide-readmore{margin-top:8px;padding:0;border:0;background:none;color:#fff;font:inherit;font-size:14px;font-weight:700;text-decoration:underline;text-underline-offset:3px;cursor:pointer;}',
      '.stories__progress{position:absolute;top:0;left:0;right:0;z-index:20;display:flex;gap:4px;padding:10px 12px;}',
      '.stories__progress-seg{position:relative;flex:1 1 0;height:3px;border-radius:3px;background:rgba(255,255,255,.3);overflow:hidden;}',
      '.stories__progress-fill{position:absolute;inset:0;width:0;background:#fff;border-radius:3px;}',
      '.stories__progress-seg.is-filled .stories__progress-fill{width:100%;}',
      '.stories__rail{position:absolute;right:14px;bottom:120px;z-index:20;display:flex;flex-direction:column;gap:20px;align-items:center;}',
      '.stories__rail-btn{background:none;border:0;color:#fff;cursor:pointer;display:flex;flex-direction:column;align-items:center;gap:4px;font:inherit;line-height:1;padding:0;}',
      '.stories__like-icon,.stories__sound-icon,.stories__more{font-size:26px;}',
      '.stories__like.is-liked .stories__like-icon{color:#ff4d67;}',
      '.stories__like-count{font-size:12px;font-weight:600;}',
      '.stories__like-icon.is-bump{animation:storiesLikeBump .35s ease;}',
      '.stories__sound.is-disabled{opacity:.35;}',
      '@keyframes storiesLikeBump{0%{transform:scale(1);}40%{transform:scale(1.35);}100%{transform:scale(1);}}',
      '.stories__player-close{position:absolute;top:8px;right:12px;z-index:30;background:none;border:0;color:#fff;font-size:30px;line-height:1;cursor:pointer;}',
      '.stories__change-topic{position:absolute;left:16px;bottom:28px;z-index:30;background:rgba(0,0,0,.45);color:#fff;border:1px solid rgba(255,255,255,.35);border-radius:999px;padding:10px 16px;font-weight:700;cursor:pointer;}',
      '.stories__sheet{position:fixed;inset:0;z-index:70;}',
      '.stories__sheet[hidden]{display:none;}',
      '.stories__sheet-pill.is-selected{background:var(--pill-color,#00d282);color:var(--pill-text,#04181e);}',
      '@media (prefers-reduced-motion: reduce){.stories__slides{scroll-behavior:auto;}}'
    ].join('');
    var style = document.createElement('style');
    style.id = RUNTIME_STYLE_ID;
    style.textContent = css;
    document.head.appendChild(style);
  }

  /* ============================================================== class */

  function StoriesCampaign(root) {
    if (!root || root.__storiesInstance) return root && root.__storiesInstance;
    root.__storiesInstance = this;

    this.root = root;
    this.seenKey = root.getAttribute('data-seen-key') || 'pulsetto_stories_v1_seen';
    // Derive a companion key for persisted likes from the seen key namespace.
    this.likesKey = this.seenKey.replace(/_seen$/, '_likes');
    if (this.likesKey === this.seenKey) this.likesKey = this.seenKey + '_likes';

    this.reduced = prefersReducedMotion();

    // --- element refs (server markup) ---
    this.hero = root.querySelector('[data-stories-hero]');
    this.heroCta = root.querySelector('[data-hero-cta]');
    this.grid = root.querySelector('[data-stories-grid]');
    this.filterPills = Array.prototype.slice.call(root.querySelectorAll('[data-filter-pill]'));
    this.player = root.querySelector('[data-stories-player]');
    this.track = this.player ? this.player.querySelector('[data-player-track]') : null;
    this.playerClose = this.player ? this.player.querySelector('[data-player-close]') : null;
    this.changeTopicBtn = this.player ? this.player.querySelector('[data-change-topic]') : null;
    this.sheet = root.querySelector('[data-topic-sheet]');
    this.sheetTabs = this.sheet ? Array.prototype.slice.call(this.sheet.querySelectorAll('[data-sheet-tab]')) : [];
    this.sheetPills = this.sheet ? Array.prototype.slice.call(this.sheet.querySelectorAll('[data-sheet-pill]')) : [];
    this.sheetClear = this.sheet ? this.sheet.querySelector('[data-sheet-clear]') : null;
    this.sheetCloseEls = this.sheet ? Array.prototype.slice.call(this.sheet.querySelectorAll('[data-sheet-close]')) : [];
    this.sheetCount = this.sheet ? this.sheet.querySelector('[data-sheet-count]') : null;
    this.sheetPanel = this.sheet ? this.sheet.querySelector('.stories__sheet-panel') : null;
    this._sheetHideTimer = null;

    if (!this.grid || !this.player || !this.track) return; // markup incomplete — bail safely

    injectRuntimeCSS();

    // --- state ---
    this.model = this.buildModel();          // all stories
    this.set = this.model.slice();           // current player set (filtered)
    this.slideModel = [];                    // slide descriptors (media | text group)
    this.slides = [];                        // slide elements for current set
    this.segs = [];                          // progress segment elements
    this.activeIndex = -1;
    this.muted = true;                       // videos start muted (autoplay policy)
    this.filter = [];                        // active (applied) category handles ([] = all)
    this.sheetSel = [];                      // PENDING sheet selection (applied only on "Filter")
    this.sheetTab = 'all';
    this.playerOpen = false;
    this.sheetOpen = false;
    this._advTimer = null;
    this._playerBuiltUI = false;
    this._videoPlayLogged = {};              // story_id -> true (dedupe play GA)
    this.liked = this.loadLiked();           // Set of liked story ids

    // "Send us your story" note (editor richtext, server-rendered as the desktop pill).
    // firstVisit is read before showHero() sets the seen flag.
    this.firstVisit = !lsGet(this.seenKey);
    var noteBody = root.querySelector('[data-stories-note] .stories__note');
    this.noteHTML = noteBody ? noteBody.innerHTML : '';
    this._withNote = false;                  // prepend the note slide on the next renderPlayer()
    this._noteUsed = false;                  // note slide is shown at most once per page view

    // Bound handlers we must be able to remove on destroy.
    this._onKeydown = this.onKeydown.bind(this);
    this._onResize = debounce(this.onResize.bind(this), 150);
    this._onScroll = debounce(this.onScrollSettle.bind(this), 90);

    this.bindStaticEvents();
    // The wall is display:none below 768px — mobile boots into the player — so
    // reordering ~100 tiles and observing their videos there is pure wasted work
    // at boot. Set it up when (and if) the grid is actually shown.
    this.setupGridWhenVisible();

    // Boot: returning-visitor gate vs hero+grid.
    var seen = !!lsGet(this.seenKey);
    if (seen && device() === 'mobile' && this.set.length) {
      // Boot straight into the player (screenshot #3 state).
      this.open(0, 'returning');
    } else {
      this.showHero();
    }
  }

  /* ------------------------------------------------------------- model */

  StoriesCampaign.prototype.buildModel = function () {
    var nodes = Array.prototype.slice.call(this.grid.querySelectorAll('article[data-story]'));
    return nodes.map(function (el) {
      var full = el.querySelector('.stories__card-full');
      return {
        el: el,
        id: el.getAttribute('data-story-id') || '',
        type: el.getAttribute('data-story-type') || 'text',
        orientation: el.getAttribute('data-orientation') || '',
        category: el.getAttribute('data-category') || '',
        categoryName: el.getAttribute('data-category-name') || '',
        categoryColor: el.getAttribute('data-category-color') || '#00d282',
        categoryTextColor: el.getAttribute('data-category-text-color') || '#04181e',
        group: el.getAttribute('data-group') || '',
        quote: el.getAttribute('data-quote') || '',
        author: el.getAttribute('data-author') || '',
        context: el.getAttribute('data-context') || '',
        like: toInt(el.getAttribute('data-like'), 0),
        duration: toFloat(el.getAttribute('data-duration'), 6),
        image: el.getAttribute('data-image') || '',
        images: (el.getAttribute('data-images') || '').split('|').filter(function (u) { return !!u; }),
        poster: el.getAttribute('data-poster') || '',
        video: el.getAttribute('data-video') || '',
        bodyHTML: full ? full.innerHTML : '',
        // Plain text straight off the element — no HTML parsing, entities already decoded.
        bodyText: full ? (full.textContent || full.innerText || '') : ''
      };
    });
  };

  StoriesCampaign.prototype.loadLiked = function () {
    var set = {};
    var raw = lsGet(this.likesKey);
    if (raw) {
      try {
        var arr = JSON.parse(raw);
        if (Array.isArray(arr)) arr.forEach(function (id) { set[id] = true; });
      } catch (e) { /* ignore corrupt value */ }
    }
    return set;
  };
  StoriesCampaign.prototype.saveLiked = function () {
    var ids = [];
    for (var id in this.liked) if (this.liked[id]) ids.push(id);
    lsSet(this.likesKey, JSON.stringify(ids));
  };
  StoriesCampaign.prototype.isLiked = function (story) { return !!this.liked[story.id]; };
  StoriesCampaign.prototype.likeDisplay = function (story) {
    return story.like + (this.isLiked(story) ? 1 : 0);
  };

  StoriesCampaign.prototype.markSeen = function () {
    if (!lsGet(this.seenKey)) lsSet(this.seenKey, String(Date.now()));
  };

  /* ---------------------------------------------------------- hero/grid */

  StoriesCampaign.prototype.showHero = function () {
    // Fire the load-state analytics for the hero+grid experience.
    pushDL('stories_grid_view', { device: device() });
    pushDL('stories_hero_view', { device: device(), story_count: this.model.length });

    // Once the hero has been shown, remember it so the next visit boots
    // straight into the player (returning-visitor gate).
    this.markSeen();

    // Mobile first-visit swipe-up cue -> open player.
    if (this.heroCta) {
      this._heroCtaClick = function () {
        pushDL('stories_swipe_up', {});
        this.open(0, 'swipe');
      }.bind(this);
      this.heroCta.addEventListener('click', this._heroCtaClick);
    }
    // Swipe-up gesture anywhere on the hero (mobile).
    if (this.hero) {
      var startY = null;
      this._heroTouchStart = function (e) {
        startY = e.touches && e.touches[0] ? e.touches[0].clientY : null;
      };
      this._heroTouchEnd = function (e) {
        if (startY === null) return;
        var endY = e.changedTouches && e.changedTouches[0] ? e.changedTouches[0].clientY : startY;
        if (startY - endY > 50 && device() === 'mobile') {
          pushDL('stories_swipe_up', {});
          this.open(0, 'swipe');
        }
        startY = null;
      }.bind(this);
      this.hero.addEventListener('touchstart', this._heroTouchStart, { passive: true });
      this.hero.addEventListener('touchend', this._heroTouchEnd, { passive: true });
    }
  };

  /* --------------------------------------------------------- filtering */

  // Compute the story set for a set of category handles ([] = all).
  StoriesCampaign.prototype.storiesFor = function (handles) {
    if (!handles || !handles.length) return this.model.slice();
    var wanted = {};
    handles.forEach(function (h) { wanted[h] = true; });
    var out = this.model.filter(function (s) { return wanted[s.category]; });
    return out.length ? out : this.model.slice(); // guard empty result
  };

  // Central filter setter. `opts.fireApply` pushes stories_filter_apply.
  StoriesCampaign.prototype.setFilter = function (handles, opts) {
    opts = opts || {};
    // normalise: drop "all", dedupe
    var clean = [];
    (handles || []).forEach(function (h) {
      if (h && h !== 'all' && clean.indexOf(h) === -1) clean.push(h);
    });
    this.filter = clean;
    this.sheetSel = clean.slice(); // applied filter and pending selection re-sync on apply

    this.set = this.storiesFor(clean);
    this.filterGrid();
    this.syncFilterPills();
    this.syncSheetPills();
    this.updateSheetCount();
    this.updateSheetTabs();

    if (this.playerOpen) {
      var prev = this.activeIndex;
      this.renderPlayer();
      var keep = clamp(prev, 0, this.slideModel.length - 1);
      this.activeIndex = -1; // force re-activation onto the freshly built slides
      this.goToIndex(keep, true);
    }

    if (opts.fireApply) {
      // Comma-string, not an array: GA4 event parameters accept strings and
      // numbers only, so an array arrives unusable or is dropped outright.
      // docs/stories-ga4-tracking.md specified this shape from the start.
      // Empty selection ("all topics") stays an empty string.
      pushDL('stories_filter_apply', { categories: clean.join(','), count: this.set.length });
    }
  };

  StoriesCampaign.prototype.filterGrid = function () {
    var wanted = null;
    if (this.filter.length) {
      wanted = {};
      this.filter.forEach(function (h) { wanted[h] = true; });
    }
    this.model.forEach(function (s) {
      s.el.style.display = (!wanted || wanted[s.category]) ? '' : 'none';
    });
  };

  StoriesCampaign.prototype.syncFilterPills = function () {
    var filter = this.filter;
    this.filterPills.forEach(function (pill) {
      var cat = pill.getAttribute('data-category');
      var on = cat === 'all' ? filter.length === 0 : filter.indexOf(cat) !== -1;
      pill.classList.toggle('is-active', on);
    });
  };

  // Sheet pills reflect the PENDING selection (this.sheetSel), not the applied
  // filter — selecting a pill doesn't filter until the user hits "Filter".
  StoriesCampaign.prototype.syncSheetPills = function () {
    var sel = this.sheetSel;
    this.sheetPills.forEach(function (pill) {
      var cat = pill.getAttribute('data-category');
      var on = sel.indexOf(cat) !== -1;
      pill.classList.toggle('is-selected', on);
      // Inline colour so the selected chip shows its category colour even
      // before the CSS dev styles .is-selected (uses the inline --pill-* vars).
      if (on) {
        pill.style.background = 'var(--pill-color)';
        pill.style.color = 'var(--pill-text)';
      } else {
        pill.style.background = '';
        pill.style.color = '';
      }
    });
  };

  StoriesCampaign.prototype.updateSheetCount = function () {
    if (!this.sheetCount) return;
    var total = this.sheetPills.length;
    var n = this.sheetSel.length;
    this.sheetCount.textContent = n === 0
      ? 'Showing all ' + total + ' topics'
      : 'Showing ' + n + ' of ' + total + ' topics';
  };

  // Highlight the tabs: with a pending selection, the "Filter" button lights up
  // (green CTA) to signal the user must press it to apply; otherwise "See all"
  // reads as the active/default state.
  StoriesCampaign.prototype.updateSheetTabs = function () {
    var pending = this.sheetSel.length > 0;
    this.sheetTabs.forEach(function (btn) {
      var tab = btn.getAttribute('data-sheet-tab');
      var isFilter = tab === 'filter';
      btn.classList.toggle('is-active', isFilter ? pending : !pending);
      btn.classList.toggle('is-cta', isFilter && pending);
    });
  };

  /* ----------------------------------------------------- player: build */

  // Create the persistent player chrome (progress bar + control rail) once.
  StoriesCampaign.prototype.ensurePlayerUI = function () {
    if (this._playerBuiltUI) return;

    // Progress bar (segments filled in renderPlayer).
    this.progressEl = document.createElement('div');
    this.progressEl.className = 'stories__progress';
    this.progressEl.setAttribute('data-progress', '');
    this.player.insertBefore(this.progressEl, this.player.firstChild);

    // Control rail (More / Like / Sound).
    this.railEl = document.createElement('div');
    this.railEl.className = 'stories__rail';
    this.railEl.setAttribute('data-rail', '');
    var ICON_MORE =
      '<svg viewBox="8 6 27 27" width="27" height="27" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">' +
        '<path fill="currentColor" d="M31.7695 17C31.2391 17 30.7304 17.2107 30.3553 17.5858C29.9802 17.9609 29.7695 18.4696 29.7695 19C29.7695 19.5304 29.9802 20.0391 30.3553 20.4142C30.7304 20.7893 31.2391 21 31.7695 21C32.3 21 32.8087 20.7893 33.1837 20.4142C33.5588 20.0391 33.7695 19.5304 33.7695 19C33.7695 18.4696 33.5588 17.9609 33.1837 17.5858C32.8087 17.2107 32.3 17 31.7695 17ZM21.7695 17C21.2391 17 20.7304 17.2107 20.3553 17.5858C19.9802 17.9609 19.7695 18.4696 19.7695 19C19.7695 19.5304 19.9802 20.0391 20.3553 20.4142C20.7304 20.7893 21.2391 21 21.7695 21C22.3 21 22.8087 20.7893 23.1837 20.4142C23.5588 20.0391 23.7695 19.5304 23.7695 19C23.7695 18.4696 23.5588 17.9609 23.1837 17.5858C22.8087 17.2107 22.3 17 21.7695 17ZM11.7695 17C11.2391 17 10.7304 17.2107 10.3553 17.5858C9.98024 17.9609 9.76953 18.4696 9.76953 19C9.76953 19.5304 9.98024 20.0391 10.3553 20.4142C10.7304 20.7893 11.2391 21 11.7695 21C12.3 21 12.8087 20.7893 13.1837 20.4142C13.5588 20.0391 13.7695 19.5304 13.7695 19C13.7695 18.4696 13.5588 17.9609 13.1837 17.5858C12.8087 17.2107 12.3 17 11.7695 17Z"/>' +
      '</svg>';
    var ICON_HEART =
      '<svg viewBox="8 77 27 27" width="27" height="27" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">' +
        '<path d="M21.5004 99.5C21.5004 99.5 13.6254 94.55 13.6254 89.0375C13.6129 88.1183 13.8821 87.2172 14.3971 86.4556C14.912 85.694 15.6478 85.1084 16.5056 84.7775C17.3633 84.4467 18.3018 84.3865 19.1948 84.605C20.0878 84.8236 20.8924 85.3104 21.5004 86C21.7372 85.2064 22.1883 84.4936 22.8042 83.9399C23.42 83.3863 24.1767 83.0133 24.991 82.8621C25.8052 82.7109 26.6453 82.7873 27.4189 83.0829C28.1925 83.3785 28.8695 83.8819 29.3754 84.5375C29.3754 90.05 21.5004 99.5 21.5004 99.5Z" stroke="currentColor" stroke-width="2.1375" stroke-linecap="round" stroke-linejoin="round"/>' +
      '</svg>';
    var ICON_SOUND =
      '<svg viewBox="9.76 148 27 27" width="27" height="27" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">' +
        '<path d="M22.1348 153.625L16.5098 158.125H13.1348V164.875H16.5098L22.1348 169.375V153.625Z" stroke="currentColor" stroke-width="2.1375" stroke-linecap="round" stroke-linejoin="round"/>' +
        '<path d="M27.7598 158.125C28.2386 158.547 28.6221 159.067 28.8848 159.649C29.1474 160.23 29.2833 160.862 29.2833 161.5C29.2833 162.138 29.1474 162.77 28.8848 163.351C28.6221 163.933 28.2386 164.453 27.7598 164.875" stroke="currentColor" stroke-width="2.1375" stroke-linecap="round" stroke-linejoin="round"/>' +
      '</svg>';
    this.railEl.innerHTML =
      '<button class="stories__rail-btn stories__more" data-rail-more type="button" aria-label="More">' +
        '<span class="stories__rail-icon">' + ICON_MORE + '</span>' +
        '<span class="stories__rail-label">More</span>' +
      '</button>' +
      '<button class="stories__rail-btn stories__like" data-rail-like type="button" aria-pressed="false" aria-label="Like">' +
        '<span class="stories__rail-icon stories__like-icon">' + ICON_HEART + '</span>' +
        '<span class="stories__like-count">0</span>' +
      '</button>' +
      '<button class="stories__rail-btn stories__sound is-muted" data-rail-sound type="button" aria-label="Toggle sound">' +
        '<span class="stories__rail-icon stories__sound-icon">' + ICON_SOUND + '</span>' +
        '<span class="stories__rail-label">Sound</span>' +
      '</button>';
    // insert rail before the change-topic button so change-topic stays bottom-left
    this.player.appendChild(this.railEl);

    this.moreBtn = this.railEl.querySelector('[data-rail-more]');
    this.likeBtn = this.railEl.querySelector('[data-rail-like]');
    this.likeIcon = this.railEl.querySelector('.stories__like-icon');
    this.likeCount = this.railEl.querySelector('.stories__like-count');
    this.soundBtn = this.railEl.querySelector('[data-rail-sound]');
    this.soundIcon = this.railEl.querySelector('.stories__sound-icon');

    this.moreBtn.addEventListener('click', this.onMore.bind(this));
    this.likeBtn.addEventListener('click', this.onLike.bind(this));
    this.soundBtn.addEventListener('click', this.onSound.bind(this));

    // "More" action menu (TikTok-style): Share / Copy link / Read story.
    // Feather-style line icons (24x24, currentColor stroke).
    var SVG_OPEN = '<svg viewBox="0 0 24 24" width="20" height="20" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">';
    var ICON_SHARE = SVG_OPEN + '<path d="M4 12v8a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2v-8"/><polyline points="16 6 12 2 8 6"/><line x1="12" y1="2" x2="12" y2="15"/></svg>';
    var ICON_LINK = SVG_OPEN + '<path d="M10 13a5 5 0 0 0 7.54.54l3-3a5 5 0 0 0-7.07-7.07l-1.72 1.71"/><path d="M14 11a5 5 0 0 0-7.54-.54l-3 3a5 5 0 0 0 7.07 7.07l1.71-1.71"/></svg>';
    var ICON_BOOK = SVG_OPEN + '<path d="M2 3h6a4 4 0 0 1 4 4v14a3 3 0 0 0-3-3H2z"/><path d="M22 3h-6a4 4 0 0 0-4 4v14a3 3 0 0 1 3-3h7z"/></svg>';
    this.moreMenu = document.createElement('div');
    this.moreMenu.className = 'stories__more-menu';
    this.moreMenu.hidden = true;
    this.moreMenu.innerHTML =
      '<div class="stories__more-scrim" data-more-close></div>' +
      '<div class="stories__more-panel" role="menu">' +
        '<button class="stories__more-item" data-more-share type="button" role="menuitem">' +
          '<span class="stories__more-ic">' + ICON_SHARE + '</span>Share</button>' +
        '<button class="stories__more-item" data-more-copy type="button" role="menuitem">' +
          '<span class="stories__more-ic">' + ICON_LINK + '</span>Copy link</button>' +
        '<button class="stories__more-item" data-more-read type="button" role="menuitem" hidden>' +
          '<span class="stories__more-ic">' + ICON_BOOK + '</span>Read story</button>' +
        '<button class="stories__more-item stories__more-cancel" data-more-close type="button">Cancel</button>' +
      '</div>';
    this.player.appendChild(this.moreMenu);
    this.moreMenuPanel = this.moreMenu.querySelector('.stories__more-panel');
    this.moreReadBtn = this.moreMenu.querySelector('[data-more-read]');
    this.moreMenuOpen = false;
    this._moreHideTimer = null;
    var self = this;
    this.moreMenu.querySelector('[data-more-share]').addEventListener('click', function () { self.shareActive(); });
    this.moreMenu.querySelector('[data-more-copy]').addEventListener('click', function () { self.copyActive(); });
    this.moreReadBtn.addEventListener('click', function () { self.readActive(); });
    Array.prototype.forEach.call(this.moreMenu.querySelectorAll('[data-more-close]'), function (el) {
      el.addEventListener('click', function () { self.closeMoreMenu(); });
    });

    // Pull-to-refresh indicator (shown when the user pulls down / scrolls up at
    // the very top → reshuffles the feed order).
    this.refreshEl = document.createElement('div');
    this.refreshEl.className = 'stories__refresh';
    this.refreshEl.innerHTML =
      '<span class="stories__refresh-spinner"></span>' +
      '<span class="stories__refresh-label">Pull to shuffle</span>';
    this.player.appendChild(this.refreshEl);
    this.refreshLabel = this.refreshEl.querySelector('.stories__refresh-label');

    // Full-content modal (opens when the caption is tapped / "Read story").
    this.readModal = document.createElement('div');
    this.readModal.className = 'stories__read';
    this.readModal.hidden = true;
    this.readModal.innerHTML =
      '<div class="stories__read-scrim" data-read-close></div>' +
      '<div class="stories__read-panel" role="dialog" aria-modal="true" aria-label="Story">' +
        '<button class="stories__read-close" data-read-close type="button" aria-label="Close">&times;</button>' +
        '<div class="stories__read-body"></div>' +
      '</div>';
    this.player.appendChild(this.readModal);
    this.readPanel = this.readModal.querySelector('.stories__read-panel');
    this.readBody = this.readModal.querySelector('.stories__read-body');
    this.readOpen = false;
    var selfRead = this;
    Array.prototype.forEach.call(this.readModal.querySelectorAll('[data-read-close]'), function (el) {
      el.addEventListener('click', function () { selfRead.closeRead(); });
    });

    this.track.classList.add('stories__slides');
    this.track.addEventListener('scroll', this._onScroll, { passive: true });
    this._onTextLike = this.handleTextLike.bind(this);
    this.track.addEventListener('click', this._onTextLike);
    this._onBodyTap = this.handleBodyTap.bind(this);
    this.track.addEventListener('click', this._onBodyTap);

    // "Read more" — handleBodyTap ignores buttons, so the teaser CTA needs its own.
    this._onReadMore = this.handleReadMore.bind(this);
    this.track.addEventListener('click', this._onReadMore);

    // Pull-to-refresh gestures (touch drag-down + desktop wheel-up at the top).
    this._onTrackTouchStart = this.onPullStart.bind(this);
    this._onTrackTouchMove = this.onPullMove.bind(this);
    this._onTrackTouchEnd = this.onPullEnd.bind(this);
    this._onTrackWheel = this.onPullWheel.bind(this);
    this.track.addEventListener('touchstart', this._onTrackTouchStart, { passive: true });
    this.track.addEventListener('touchmove', this._onTrackTouchMove, { passive: false });
    this.track.addEventListener('touchend', this._onTrackTouchEnd, { passive: true });
    this.track.addEventListener('wheel', this._onTrackWheel, { passive: false });

    this._playerBuiltUI = true;
  };

  // How many short text stories to pack onto one mobile slide (fit 2–3).
  StoriesCampaign.prototype.textPerSlide = function () {
    return (window.innerHeight || 0) >= 720 ? 3 : 2;
  };

  // Pack text stories 2–3 to a slide (pooled across the feed, since text stories
  // are usually interspersed with media); media stays one-per-slide, in order.
  StoriesCampaign.prototype.buildSlideModel = function () {
    var model = [];
    var per = this.textPerSlide();
    var buf = [];
    function flush() {
      if (buf.length) { model.push({ kind: 'text', stories: buf.slice() }); buf.length = 0; }
    }
    if (this._withNote) model.push({ kind: 'note' });
    for (var i = 0; i < this.set.length; i++) {
      var s = this.set[i];
      if (s.type === 'text') {
        buf.push(s);
        if (buf.length >= per) flush();
      } else {
        model.push({ kind: 'media', story: s });
      }
    }
    flush();
    return model;
  };

  // Representative story for a slide (media story, or a text group's first).
  StoriesCampaign.prototype.primaryStory = function (index) {
    var e = this.slideModel[index];
    if (!e || e.kind === 'note') return null;
    return e.kind === 'text' ? e.stories[0] : e.story;
  };

  StoriesCampaign.prototype.slideIndexForStory = function (story) {
    for (var i = 0; i < this.slideModel.length; i++) {
      var e = this.slideModel[i];
      if (e.kind === 'media' && e.story === story) return i;
      if (e.kind === 'text' && e.stories.indexOf(story) !== -1) return i;
    }
    return 0;
  };

  StoriesCampaign.prototype.findStory = function (id) {
    for (var i = 0; i < this.model.length; i++) if (this.model[i].id === id) return this.model[i];
    return null;
  };

  // (Re)build slides + progress segments for the current `this.set`.
  StoriesCampaign.prototype.renderPlayer = function () {
    this.ensurePlayerUI();
    this.clearAdvance();

    this.slideModel = this.buildSlideModel();
    this.viewedIds = {}; // fresh set → nothing viewed yet (gates the completion card)

    this.track.innerHTML = '';
    var frag = document.createDocumentFragment();
    var self = this;

    this.slideModel.forEach(function (entry, i) {
      var slide = document.createElement('article');
      slide.setAttribute('data-index', String(i));

      if (entry.kind === 'note') {
        slide.className = 'stories__slide stories__slide--end stories__slide--note';
        slide.innerHTML = '<div class="stories__end"><div class="stories__note">' + self.noteHTML + '</div></div>';
      } else if (entry.kind === 'text') {
        // A text slide packs 2–3 quote cards, each with its own like button.
        slide.className = 'stories__slide stories__slide--textgroup';
        var cards = entry.stories.map(function (story) {
          var metaLine = story.author;
          if (story.context) metaLine += (metaLine ? ' · ' : '') + story.context;
          var liked = self.isLiked(story);
          return (
            '<div class="stories__text-card" style="--cat-color:' + escAttr(story.categoryColor) +
              ';--cat-text:' + escAttr(story.categoryTextColor) + '">' +
              '<div class="stories__text-card-main">' +
                (story.categoryName ? '<span class="stories__slide-badge">' + escHTML(story.categoryName) + '</span>' : '') +
                '<p class="stories__slide-quote">' + escHTML(story.quote) + '</p>' +
                (metaLine ? '<p class="stories__slide-meta">' + escHTML(metaLine) + '</p>' : '') +
              '</div>' +
              '<button class="stories__text-like' + (liked ? ' is-liked' : '') + '" type="button" ' +
                'data-text-like="' + escAttr(story.id) + '" aria-pressed="' + (liked ? 'true' : 'false') + '" aria-label="Like">' +
                '<span class="stories__like-icon">&#9829;</span>' +
                '<span class="stories__like-count">' + formatCount(self.likeDisplay(story)) + '</span>' +
              '</button>' +
            '</div>'
          );
        }).join('');
        slide.innerHTML = '<div class="stories__text-wall">' + cards + '</div>';
      } else {
        var story = entry.story;
        slide.className = 'stories__slide stories__slide--' + story.type;
        slide.setAttribute('data-story-id', story.id);
        slide.setAttribute('data-story-type', story.type);
        slide.style.setProperty('--cat-color', story.categoryColor);
        slide.style.setProperty('--cat-text', story.categoryTextColor);

        var mediaHTML = '';
        if (story.type === 'video' && story.video) {
          // loop: no auto-advance, so the active video repeats until the user swipes
          // src is attached by syncVideoWindow() only near the active slide:
          // dozens of live <video> elements exhaust the iOS decoder budget
          // (later ones silently refuse to play) and stall mobile scrolling.
          // The POSTER is held back the same way (data-poster). A poster is not
          // covered by preload="none" — the browser fetches it as soon as the
          // element exists — so shipping it inline meant opening the player fired
          // one image request per video story at once, starving the slide the user
          // is actually looking at.
          var posterAttr = story.poster ? ' data-poster="' + escAttr(story.poster) + '"' : '';
          mediaHTML =
            '<div class="stories__slide-media">' +
              '<video class="stories__slide-video" data-src="' + escAttr(story.video) + '"' + posterAttr +
                ' playsinline muted loop preload="none"></video>' +
            '</div>';
        } else if (story.type === 'photo' && story.images.length > 1) {
          // Multiple images → a plain horizontal swipe gallery (progress bar tracks it).
          var imgsHTML = story.images.map(function (u, gi) {
            return '<img class="stories__slide-img" data-src="' + escAttr(u) + '" data-gi="' + gi + '"' +
              ' alt="' + escAttr(story.quote) + '" decoding="async">';
          }).join('');
          mediaHTML = '<div class="stories__slide-media stories__slide-media--gallery" data-gallery>' + imgsHTML + '</div>';
        } else if (story.image || story.poster) {
          mediaHTML =
            '<div class="stories__slide-media">' +
              '<img class="stories__slide-img" data-src="' + escAttr(story.image || story.poster) + '" data-gi="0"' +
                ' alt="' + escAttr(story.quote) + '" decoding="async">' +
            '</div>';
        }

        var metaLine = story.author;
        if (story.context) metaLine += (metaLine ? ' · ' : '') + story.context;

        slide.innerHTML =
          mediaHTML +
          '<div class="stories__slide-scrim"></div>' +
          '<div class="stories__slide-body">' +
            (story.categoryName ? '<span class="stories__slide-badge">' + escHTML(story.categoryName) + '</span>' : '') +
            '<p class="stories__slide-quote">' + escHTML(story.quote) + '</p>' +
            (metaLine ? '<p class="stories__slide-meta">' + escHTML(metaLine) + '</p>' : '') +
            (story.bodyHTML
              ? '<p class="stories__slide-excerpt">' + escHTML(excerptFrom(story.bodyText)) + '</p>' +
                '<button class="stories__slide-readmore" type="button" data-slide-read>' + READ_MORE_LABEL + '</button>'
              : '') +
          '</div>';

        var video = slide.querySelector('video');
        if (video) self.bindVideo(video, i);
        var mediaEls = slide.querySelectorAll('.stories__slide-video, .stories__slide-img');
        for (var mi = 0; mi < mediaEls.length; mi++) applyMediaOrientation(mediaEls[mi], slide, story.orientation);
      }

      frag.appendChild(slide);
    });
    this.track.appendChild(frag);

    // End card — after the last real story, suggest picking a topic (opens sheet).
    this.endSlide = this.buildEndSlide();
    this.track.appendChild(this.endSlide);
    this._atEnd = false;
    this._endLogged = false;

    this.slides = Array.prototype.slice.call(this.track.querySelectorAll('.stories__slide'));
    // Cache the media elements per slide — activate() runs on every swipe and
    // must not re-query the whole track.
    this.slides.forEach(function (el) {
      el._video = el.querySelector('video');
      el._imgs = Array.prototype.slice.call(el.querySelectorAll('img[data-src]'));
    });
    this._activeVideo = null;

    // Progress is built per active slide (only for multi-image photo galleries).
    this.progressEl.innerHTML = '';
    this.progressEl.style.display = 'none';
    this.segs = [];
  };

  // The trailing "you've reached the end — pick a topic" card. It lives just past
  // the last real slide in the scroll-snap track; its button opens the topic sheet.
  StoriesCampaign.prototype.buildEndSlide = function () {
    var self = this;
    var el = document.createElement('article');
    el.className = 'stories__slide stories__slide--end';
    el.setAttribute('data-index', String(this.slideModel.length));
    el.innerHTML =
      '<div class="stories__end">' +
        '<p class="stories__end-kicker">You’re all caught up</p>' +
        '<h2 class="stories__end-title">Want more? Pick a topic.</h2>' +
        '<p class="stories__end-sub">Filter the wall to the moments closest to your own.</p>' +
        '<button class="stories__end-btn" type="button" data-end-topics>Choose a topic</button>' +
        (this.noteHTML ? '<div class="stories__note stories__note--end">' + this.noteHTML + '</div>' : '') +
      '</div>';
    var btn = el.querySelector('[data-end-topics]');
    if (btn) btn.addEventListener('click', function () {
      pushDL('stories_end_pick_topic', {});
      self.openSheet();
    });
    return el;
  };

  StoriesCampaign.prototype.bindVideo = function (video, index) {
    var self = this;
    video.addEventListener('play', function () {
      if (index !== self.activeIndex) return;
      var story = self.primaryStory(index);
      var id = story && story.id;
      if (id && !self._videoPlayLogged[id]) {
        self._videoPlayLogged[id] = true;
        pushDL('stories_video_play', { story_id: id });
      }
    });
    // Auto-advance removed: the active video loops; navigation is manual.
  };

  /* ------------------------------------------------------ player: open */

  StoriesCampaign.prototype.open = function (storyIndex, source) {
    if (!this.set.length) return;
    storyIndex = clamp(storyIndex || 0, 0, this.set.length - 1);

    // First-time mobile visitors swiping up from the hero land on the note slide first.
    this._withNote = source === 'swipe' && this.firstVisit && !!this.noteHTML && !this._noteUsed;
    this.renderPlayer();
    var slideIndex = this._withNote ? 0 : this.slideIndexForStory(this.set[storyIndex]);
    if (this._withNote) this._noteUsed = true;
    this._withNote = false; // later re-renders (filter, shuffle) drop the note

    // Attach the first slide's media BEFORE the player is revealed. activate()
    // does not run until the next frame (it needs layout for clientHeight), so
    // without this the player paints one black frame and fills in a beat later —
    // most visible on mobile, where a returning visitor boots straight in here.
    this.syncImageWindow(slideIndex);

    this.player.hidden = false;
    this.player.setAttribute('aria-hidden', 'false');
    this.player.classList.add('is-open');
    if (!this.playerOpen) lockScroll();
    this.playerOpen = true;

    document.addEventListener('keydown', this._onKeydown);
    window.addEventListener('resize', this._onResize);

    this.syncCloseBtn();
    this.markSeen();

    var self = this;
    // Wait a frame so the player has layout (clientHeight) before we scroll.
    requestAnimationFrame(function () {
      var h = self.slideH();
      // Position without firing nav events, then activate directly.
      self.track.scrollTop = slideIndex * h;
      self.activeIndex = -1; // ensure activate() runs
      self.activate(slideIndex);

      var story = self.primaryStory(slideIndex);
      pushDL('stories_player_open', {
        source: source || 'grid',
        story_id: story ? story.id : null,
        story_type: story ? story.type : 'note',
        category: story ? story.category : null,
        position: slideIndex + 1
      });
    });
  };

  StoriesCampaign.prototype.closePlayer = function () {
    if (!this.playerOpen) return;
    if (this.moreMenuOpen) { this.moreMenu.classList.remove('is-open'); this.moreMenu.hidden = true; this.moreMenuOpen = false; }
    if (this.readOpen) { this.readModal.classList.remove('is-open'); this.readModal.hidden = true; this.readOpen = false; }
    this.clearAdvance();
    this.pauseAllVideos();
    this.player.hidden = true;
    this.player.setAttribute('aria-hidden', 'true');
    this.player.classList.remove('is-open');
    this.playerOpen = false;
    unlockScroll();
    document.removeEventListener('keydown', this._onKeydown);
    window.removeEventListener('resize', this._onResize);
  };

  /* ------------------------------------------------ player: navigation */

  // Keep a loaded source on the active slide and its immediate neighbours only.
  // Everything further out is paused and detached: iOS caps how many videos can
  // decode at once (extra ones just never start), and holding metadata for dozens
  // of files is what makes the feed stutter on mobile.
  StoriesCampaign.prototype.syncVideoWindow = function (index) {
    var KEEP = 1;   // attach src within this many slides of the active one
    var DROP = 2;   // detach beyond this many
    for (var i = 0; i < this.slides.length; i++) {
      var v = this.slides[i]._video;
      if (!v) continue;
      var src = v.getAttribute('data-src');
      if (!src) continue;
      var dist = Math.abs(i - index);
      if (dist <= KEEP) {
        if (v.getAttribute('src') !== src) v.setAttribute('src', src);
        // The active one streams. The slide BELOW is the one nearly every swipe
        // goes to, so it buffers fully too — that is the difference between the
        // next story playing instantly and it sitting on a frozen poster while
        // the file downloads. The slide above only fetches its header; scrolling
        // back up is rare and it is already in cache from the way down.
        v.preload = (dist === 0 || i === index + 1) ? 'auto' : 'metadata';
      } else if (dist > DROP && v.getAttribute('src')) {
        try { v.pause(); } catch (e) { /* ignore */ }
        v.removeAttribute('src');
        try { v.load(); } catch (e) { /* ignore */ }  // release the decoder
      }
    }
  };

  // Images and video posters get their own, wider window than the videos above.
  // A decoded image costs a few MB of RAM and nothing else — no decoder slot, no
  // open connection — so there is no reason to be as stingy with them, and being
  // stingy is exactly what produced the black-slide-then-pop on every swipe:
  // the slides were built with loading="lazy" inside a position:fixed scroll
  // container the browser treats as entirely offscreen, so nothing prefetched.
  //
  //   dist <= ATTACH : src is on the element, decoded, ready to paint
  //   dist <= WARM   : bytes pulled into the HTTP cache only (no DOM cost)
  //   beyond         : untouched
  StoriesCampaign.prototype.syncImageWindow = function (index) {
    var ATTACH = 2;
    var WARM = 5;
    for (var i = 0; i < this.slides.length; i++) {
      var dist = Math.abs(i - index);
      if (dist > WARM) continue;
      var slide = this.slides[i];

      var imgs = slide._imgs || [];
      for (var k = 0; k < imgs.length; k++) {
        var img = imgs[k];
        var src = img.getAttribute('data-src');
        if (!src) continue;
        // Off the active slide only the gallery's cover shot is attached — a
        // 5-shot gallery two slides away would otherwise pull 5 full-size files
        // for images the user may never swipe sideways to see.
        var wanted = dist <= ATTACH && (dist === 0 || img.getAttribute('data-gi') === '0');
        if (wanted) {
          if (img.getAttribute('src') !== src) {
            if (dist === 0) { try { img.fetchPriority = 'high'; } catch (e) { /* ignore */ } }
            img.setAttribute('src', src);
          }
        } else if (!img.getAttribute('src')) {
          warmCache(src);
        }
      }

      // Posters ride the image window, not the video one: without a poster a
      // video slide is black until the first frame decodes, which is the very
      // stall the poster exists to hide.
      var v = slide._video;
      if (v) {
        var p = v.getAttribute('data-poster');
        if (p) {
          if (dist <= ATTACH) {
            if (v.getAttribute('poster') !== p) v.setAttribute('poster', p);
          } else if (!v.getAttribute('poster')) {
            warmCache(p);
          }
        }
      }
    }
  };

  StoriesCampaign.prototype.slideH = function () {
    return this.track.clientHeight || window.innerHeight || 1;
  };

  // Scroll to an index. The scroll-settle listener performs activation +
  // story_next/story_prev events, so all nav funnels through one path.
  StoriesCampaign.prototype.goToIndex = function (index, instant) {
    index = clamp(index, 0, this.slideModel.length - 1);
    var top = index * this.slideH();
    if (instant || this.reduced) {
      this.track.scrollTop = top;
      this.onScrollSettle(); // no smooth-scroll events to rely on
    } else {
      try {
        this.track.scrollTo({ top: top, behavior: 'smooth' });
      } catch (e) {
        this.track.scrollTop = top;
        this.onScrollSettle();
      }
    }
  };

  StoriesCampaign.prototype.next = function () {
    if (this.activeIndex < this.slideModel.length - 1) this.goToIndex(this.activeIndex + 1);
  };
  StoriesCampaign.prototype.prev = function () {
    if (this.activeIndex > 0) this.goToIndex(this.activeIndex - 1);
  };

  // Fired (debounced) after a manual or programmatic scroll settles.
  StoriesCampaign.prototype.onScrollSettle = function () {
    if (!this.playerOpen) return;
    var h = this.slideH();
    if (!h) return;
    var raw = Math.round(this.track.scrollTop / h);

    // Trailing end card sits one slot past the last real slide — but the user
    // must view EVERY story first (random start means the bottom isn't "done").
    if (raw >= this.slideModel.length) {
      if (!this.allViewed()) {
        // Loop back to the first story they haven't seen yet, in any order.
        var back = this.firstUnviewedSlideIndex();
        var left = this.remainingCount();
        this.toast(left === 1 ? '1 more story' : left + ' more stories');
        pushDL('stories_loop_back', { remaining: left });
        this.goToIndex(back, true);
        return;
      }
      this._atEnd = true;
      if (this.railEl) this.railEl.style.display = 'none';
      this.pauseAllVideos();
      if (this.endSlide) this.endSlide.classList.add('is-active');
      if (!this._endLogged) { this._endLogged = true; pushDL('stories_completed', {}); }
      return;
    }
    if (this.endSlide) this.endSlide.classList.remove('is-active');

    var idx = clamp(raw, 0, this.slideModel.length - 1);
    if (this._atEnd) {          // scrolled back up off the end card → rebuild the rail
      this._atEnd = false;
      this.activeIndex = -1;    // force a full re-activation
      this.activate(idx);
      return;
    }
    if (idx === this.activeIndex) return;

    var from = this.primaryStory(this.activeIndex);
    var to = this.primaryStory(idx);
    var dir = idx > this.activeIndex ? 'stories_next' : 'stories_prev';
    pushDL(dir, {
      from_id: from ? from.id : null,
      to_id: to ? to.id : null,
      position: idx + 1
    });
    this.activate(idx);
  };

  // Make slide `index` the active one: media, progress, rail, analytics.
  StoriesCampaign.prototype.activate = function (index) {
    if (index === this.activeIndex) return;
    this.activeIndex = index;
    var entry = this.slideModel[index];
    if (!entry) return;
    this.markViewed(entry);

    // Slide active state — class work only, cheap across the whole list.
    for (var i = 0; i < this.slides.length; i++) {
      this.slides[i].classList.toggle('is-active', i === index);
    }

    // Media: load sources only around the active slide, and touch just the
    // outgoing and incoming videos rather than every slide on the track.
    this.syncVideoWindow(index);
    this.syncImageWindow(index);
    var next = this.slides[index] ? this.slides[index]._video : null;
    var prev = this._activeVideo;
    if (prev && prev !== next) {
      try { prev.pause(); prev.currentTime = 0; } catch (e) { /* ignore */ }
    }
    this._activeVideo = next || null;
    if (next) {
      next.muted = this.muted;
      if (!this.reduced) safePlay(next);
    }

    this.updateSlideProgress(index);

    if (entry.kind === 'note') {
      if (this.railEl) this.railEl.style.display = 'none';
      pushDL('stories_note_view', { position: index + 1 });
    } else if (entry.kind === 'media') {
      // Single-story slide: use the persistent like/sound/more rail.
      if (this.railEl) this.railEl.style.display = '';
      this.updateRail(entry.story);
      pushDL('stories_view', {
        story_id: entry.story.id,
        story_type: entry.story.type,
        category: entry.story.category,
        position: index + 1
      });
    } else {
      // Text group: per-card likes live in the slide, so hide the rail.
      if (this.railEl) this.railEl.style.display = 'none';
      entry.stories.forEach(function (s) {
        pushDL('stories_view', { story_id: s.id, story_type: 'text', category: s.category, position: index + 1 });
      });
    }
  };

  /* ---------------------------------------- viewed-all gating (completion) */

  StoriesCampaign.prototype.storiesInSlide = function (entry) {
    if (!entry || entry.kind === 'note') return [];
    return entry.kind === 'text' ? entry.stories : [entry.story];
  };
  StoriesCampaign.prototype.markViewed = function (entry) {
    var v = this.viewedIds || (this.viewedIds = {});
    this.storiesInSlide(entry).forEach(function (s) { if (s) v[s.id] = true; });
  };
  StoriesCampaign.prototype.allViewed = function () {
    var v = this.viewedIds || {};
    return this.set.length > 0 && this.set.every(function (s) { return v[s.id]; });
  };
  StoriesCampaign.prototype.remainingCount = function () {
    var v = this.viewedIds || {}, n = 0;
    this.set.forEach(function (s) { if (!v[s.id]) n++; });
    return n;
  };
  StoriesCampaign.prototype.firstUnviewedSlideIndex = function () {
    var v = this.viewedIds || {};
    for (var i = 0; i < this.slideModel.length; i++) {
      var sts = this.storiesInSlide(this.slideModel[i]);
      for (var j = 0; j < sts.length; j++) { if (sts[j] && !v[sts[j].id]) return i; }
    }
    return 0;
  };

  /* ------------------------------ pull-to-refresh (shuffle the feed order) */

  var PULL_TRIGGER = 60; // px of (damped) pull needed to fire a shuffle

  // Track the finger 1:1: no CSS transition while dragging (.is-dragging), and
  // batch transform writes in a single rAF so touchmove never thrashes layout.
  StoriesCampaign.prototype.setPull = function (dist) {
    this._pullDist = Math.max(0, Math.min(dist, 90));
    this._pullReadyToFire = this._pullDist >= PULL_TRIGGER;
    if (!this.refreshEl || this._pullRAF) return;
    var self = this;
    this._pullRAF = requestAnimationFrame(function () {
      self._pullRAF = 0;
      var el = self.refreshEl;
      if (!el) return;
      el.classList.add('is-visible', 'is-dragging');
      el.classList.remove('is-loading');
      el.style.transform = 'translate(-50%, ' + Math.min(self._pullDist, PULL_TRIGGER) + 'px)';
      if (self.refreshLabel) {
        var txt = self._pullReadyToFire ? 'Release to shuffle' : 'Pull to shuffle';
        if (self.refreshLabel.textContent !== txt) self.refreshLabel.textContent = txt;
      }
    });
  };
  StoriesCampaign.prototype.hidePull = function () {
    if (this._pullRAF) { cancelAnimationFrame(this._pullRAF); this._pullRAF = 0; }
    if (!this.refreshEl) return;
    // Remove is-dragging so it animates (transition) back up out of view.
    this.refreshEl.classList.remove('is-visible', 'is-loading', 'is-dragging');
    this.refreshEl.style.transform = '';
    this._pullReadyToFire = false; this._pullDist = 0;
  };

  StoriesCampaign.prototype.onPullStart = function (e) {
    this._pulling = false; this._pullReady = false;
    if (!this.playerOpen || this._reloading) return;
    if (this.track.scrollTop > 0) return;
    var t = e.touches && e.touches[0];
    if (!t) return;
    this._pullStartY = t.clientY; this._pullReady = true;
  };
  StoriesCampaign.prototype.onPullMove = function (e) {
    if (!this._pullReady) return;
    var t = e.touches && e.touches[0];
    if (!t) return;
    if (this.track.scrollTop > 0) { if (this._pulling) { this.hidePull(); this._pulling = false; } return; }
    var dy = t.clientY - this._pullStartY;
    if (dy > 0) {
      this._pulling = true;
      if (e.cancelable) e.preventDefault(); // suppress native rubber-band
      this.setPull(dy * 0.55);
    } else if (this._pulling) {
      this.hidePull(); this._pulling = false;
    }
  };
  StoriesCampaign.prototype.onPullEnd = function () {
    if (!this._pulling) { this._pullReady = false; return; }
    var fire = this._pullReadyToFire;
    this._pulling = false; this._pullReady = false;
    if (fire) this.reloadFeed(); else this.hidePull();
  };
  // Desktop: wheel-up while already at the top accumulates into a shuffle.
  StoriesCampaign.prototype.onPullWheel = function (e) {
    if (!this.playerOpen || this._reloading) return;
    if (this.track.scrollTop > 0 || e.deltaY >= 0) { this._wheelUp = 0; return; }
    this._wheelUp = (this._wheelUp || 0) + (-e.deltaY);
    if (e.cancelable) e.preventDefault();
    this.setPull(Math.min(this._wheelUp * 0.5, 90));
    var self = this;
    clearTimeout(this._wheelTO);
    if (this._wheelUp > 140) { this._wheelUp = 0; this.reloadFeed(); return; }
    this._wheelTO = setTimeout(function () { self._wheelUp = 0; if (!self._reloading) self.hidePull(); }, 240);
  };

  StoriesCampaign.prototype.shuffledSet = function () {
    var a = this.set.slice();
    for (var i = a.length - 1; i > 0; i--) {
      var j = Math.floor(Math.random() * (i + 1));
      var tmp = a[i]; a[i] = a[j]; a[j] = tmp;
    }
    // For 2+ items, avoid returning the identical order so the reload is visible.
    if (a.length > 1 && a[0] === this.set[0]) { a.push(a.shift()); }
    return a;
  };

  StoriesCampaign.prototype.reloadFeed = function () {
    if (this._reloading || this.set.length < 2) { this.hidePull(); return; }
    this._reloading = true;
    if (this._pullRAF) { cancelAnimationFrame(this._pullRAF); this._pullRAF = 0; }
    if (this.refreshEl) {
      this.refreshEl.classList.remove('is-dragging');   // re-enable transition → smooth settle
      this.refreshEl.classList.add('is-visible', 'is-loading');
      this.refreshEl.style.transform = '';
      if (this.refreshLabel) this.refreshLabel.textContent = 'Shuffling…';
    }
    this.set = this.shuffledSet(); // new random order
    var self = this;
    setTimeout(function () {
      self.renderPlayer();       // rebuilds slides (resets viewed) from the new order
      self.activeIndex = -1;
      self.goToIndex(0, true);   // start at the top of the reshuffled feed
      self.hidePull();
      self._reloading = false;
      pushDL('stories_feed_reload', { count: self.set.length });
    }, 500);
  };

  /* -------------------------------------------------- progress / timer */

  StoriesCampaign.prototype.clearAdvance = function () {
    if (this._advTimer) { clearTimeout(this._advTimer); this._advTimer = null; }
  };

  // Build the progress bar for the active slide — ONLY for a multi-image photo
  // gallery. The segments track which image the horizontal swipe is on. Every
  // other slide type (single image, video, text) shows no progress bar.
  StoriesCampaign.prototype.updateSlideProgress = function (index) {
    var slide = this.slides[index];
    var gallery = slide ? slide.querySelector('[data-gallery]') : null;
    var imgs = gallery ? gallery.querySelectorAll('.stories__slide-img') : [];

    this.progressEl.innerHTML = '';
    this.segs = [];

    if (imgs.length < 2) { this.progressEl.style.display = 'none'; return; }
    this.progressEl.style.display = '';

    var frag = document.createDocumentFragment();
    for (var i = 0; i < imgs.length; i++) {
      var seg = document.createElement('div');
      seg.className = 'stories__progress-seg';
      seg.innerHTML = '<span class="stories__progress-fill"></span>';
      frag.appendChild(seg);
    }
    this.progressEl.appendChild(frag);
    this.segs = Array.prototype.slice.call(this.progressEl.querySelectorAll('.stories__progress-seg'));

    var self = this;
    var paint = function () {
      var w = gallery.clientWidth || 1;
      var k = clamp(Math.round(gallery.scrollLeft / w), 0, self.segs.length - 1);
      for (var j = 0; j < self.segs.length; j++) {
        var fill = self.segs[j].querySelector('.stories__progress-fill');
        self.segs[j].classList.toggle('is-filled', j <= k);
        self.segs[j].classList.toggle('is-active', j === k);
        if (fill) { fill.style.transition = 'none'; fill.style.width = j <= k ? '100%' : '0%'; }
      }
    };
    paint();
    if (!gallery._progWired) {
      gallery.addEventListener('scroll', function () { if (self.activeIndex === index) paint(); }, { passive: true });
      gallery._progWired = true;
    }
  };

  /* ------------------------------------------------------------- rail */

  StoriesCampaign.prototype.updateRail = function (story) {
    if (!this.railEl) return;
    // like
    var liked = this.isLiked(story);
    this.likeBtn.classList.toggle('is-liked', liked);
    this.likeBtn.setAttribute('aria-pressed', liked ? 'true' : 'false');
    this.likeCount.textContent = formatCount(this.likeDisplay(story));
    // sound (only meaningful on video)
    var isVideo = story.type === 'video';
    this.soundBtn.classList.toggle('is-disabled', !isVideo);
    this.soundBtn.classList.toggle('is-muted', this.muted);
    // More is always available now (it offers Share / Copy link too, not just Read).
    this.moreBtn.style.display = '';
  };

  StoriesCampaign.prototype.onLike = function () {
    var story = this.primaryStory(this.activeIndex);
    if (!story) return;
    var nowLiked = !this.isLiked(story);
    if (nowLiked) this.liked[story.id] = true; else delete this.liked[story.id];
    this.saveLiked();

    this.likeBtn.classList.toggle('is-liked', nowLiked);
    this.likeBtn.setAttribute('aria-pressed', nowLiked ? 'true' : 'false');
    this.likeCount.textContent = formatCount(this.likeDisplay(story));

    // little bump animation (retrigger)
    if (this.likeIcon) {
      this.likeIcon.classList.remove('is-bump');
      void this.likeIcon.offsetWidth;
      this.likeIcon.classList.add('is-bump');
    }
    pushDL('stories_like', { story_id: story.id, liked: nowLiked });
  };

  // Per-card like inside a grouped text slide (display-only, persisted locally).
  StoriesCampaign.prototype.handleTextLike = function (e) {
    var btn = e.target && e.target.closest ? e.target.closest('[data-text-like]') : null;
    if (!btn) return;
    var story = this.findStory(btn.getAttribute('data-text-like'));
    if (!story) return;
    var nowLiked = !this.isLiked(story);
    if (nowLiked) this.liked[story.id] = true; else delete this.liked[story.id];
    this.saveLiked();
    btn.classList.toggle('is-liked', nowLiked);
    btn.setAttribute('aria-pressed', nowLiked ? 'true' : 'false');
    var countEl = btn.querySelector('.stories__like-count');
    if (countEl) countEl.textContent = formatCount(this.likeDisplay(story));
    var icon = btn.querySelector('.stories__like-icon');
    if (icon) { icon.classList.remove('is-bump'); void icon.offsetWidth; icon.classList.add('is-bump'); }
    pushDL('stories_like', { story_id: story.id, liked: nowLiked, context: 'text' });
  };

  StoriesCampaign.prototype.onSound = function () {
    this.muted = !this.muted;
    this.pauseAllVideos(true); // apply mute to all, keep active playing
    var active = this.slides[this.activeIndex];
    var v = active ? active._video : null;
    // This runs inside the tap, so unmuted playback is allowed here — later
    // slides fall back to muted via safePlay().
    if (v) { v.muted = this.muted; if (!this.reduced) safePlay(v); }
    this.soundBtn.classList.toggle('is-muted', this.muted);
    pushDL('stories_sound_toggle', { muted: this.muted });
  };

  StoriesCampaign.prototype.onMore = function () {
    this.openMoreMenu();
  };

  StoriesCampaign.prototype.openMoreMenu = function () {
    if (!this.moreMenu) return;
    if (this._moreHideTimer) { clearTimeout(this._moreHideTimer); this._moreHideTimer = null; }
    var story = this.primaryStory(this.activeIndex);
    // "Read story" only when this story actually has body copy.
    if (this.moreReadBtn) this.moreReadBtn.hidden = !(story && story.bodyHTML);
    this.moreMenu.hidden = false;
    this.moreMenuOpen = true;
    var menu = this.moreMenu;
    if (this.reduced) { menu.classList.add('is-open'); }
    else { requestAnimationFrame(function () { requestAnimationFrame(function () { menu.classList.add('is-open'); }); }); }
    pushDL('stories_more_open', { story_id: story ? story.id : null });
  };

  StoriesCampaign.prototype.closeMoreMenu = function () {
    if (!this.moreMenu || !this.moreMenuOpen) return;
    this.moreMenu.classList.remove('is-open');
    this.moreMenuOpen = false;
    var menu = this.moreMenu;
    var panel = this.moreMenuPanel;
    var self = this;
    var hide = function () { self._moreHideTimer = null; if (!menu.classList.contains('is-open')) menu.hidden = true; };
    if (this.reduced || !panel) { hide(); return; }
    var onEnd = function (e) {
      if (e.target !== panel || e.propertyName !== 'transform') return;
      panel.removeEventListener('transitionend', onEnd);
      if (self._moreHideTimer) clearTimeout(self._moreHideTimer);
      hide();
    };
    panel.addEventListener('transitionend', onEnd);
    this._moreHideTimer = setTimeout(function () { panel.removeEventListener('transitionend', onEnd); hide(); }, 360);
  };

  StoriesCampaign.prototype.storyShareUrl = function (story) {
    return location.href.split('#')[0] + '#story-' + story.id;
  };

  StoriesCampaign.prototype.shareActive = function () {
    var story = this.primaryStory(this.activeIndex);
    if (!story) return;
    var url = this.storyShareUrl(story);
    var text = story.quote ? ('"' + story.quote + '"' + (story.author ? ' — ' + story.author : '')) : '';
    var self = this;
    if (navigator.share) {
      navigator.share({ title: document.title, text: text, url: url })
        .then(function () { pushDL('stories_share', { story_id: story.id, method: 'native' }); })
        .catch(function () { /* user dismissed — ignore */ })
        .then(function () { self.closeMoreMenu(); });
    } else {
      this.copyActive(); // no native share → fall back to copy
    }
  };

  StoriesCampaign.prototype.copyActive = function () {
    var story = this.primaryStory(this.activeIndex);
    if (!story) return;
    var url = this.storyShareUrl(story);
    var self = this;
    var done = function () { pushDL('stories_share', { story_id: story.id, method: 'copy' }); self.toast('Link copied'); self.closeMoreMenu(); };
    if (navigator.clipboard && navigator.clipboard.writeText) {
      navigator.clipboard.writeText(url).then(done).catch(function () { self.closeMoreMenu(); });
    } else {
      try {
        var t = document.createElement('textarea');
        t.value = url; t.setAttribute('readonly', ''); t.style.position = 'absolute'; t.style.left = '-9999px';
        document.body.appendChild(t); t.select(); document.execCommand('copy'); document.body.removeChild(t);
        done();
      } catch (e) { self.closeMoreMenu(); }
    }
  };

  StoriesCampaign.prototype.readActive = function () {
    this.closeMoreMenu();
    this.openRead(this.primaryStory(this.activeIndex));
  };

  // The explicit "Read more" control under the teaser → same modal as the tap.
  StoriesCampaign.prototype.handleReadMore = function (e) {
    if (!e.target || !e.target.closest) return;
    var btn = e.target.closest('[data-slide-read]');
    if (!btn) return;
    e.preventDefault();
    e.stopPropagation();
    var slide = btn.closest('.stories__slide');
    var id = slide ? slide.getAttribute('data-story-id') : null;
    var story = id ? this.findStory(id) : this.primaryStory(this.activeIndex);
    if (story) this.openRead(story);
  };

  // Tap the caption (.stories__slide-body) on a media slide → open the full
  // story in a modal. Interactive children (links/buttons) still work.
  StoriesCampaign.prototype.handleBodyTap = function (e) {
    if (!e.target || !e.target.closest) return;
    if (e.target.closest('a, button')) return;
    var body = e.target.closest('.stories__slide-body');
    if (!body) return;
    var slide = body.closest('.stories__slide');
    if (!slide || slide.classList.contains('stories__slide--textgroup')) return;
    var id = slide.getAttribute('data-story-id');
    var story = id ? this.findStory(id) : this.primaryStory(this.activeIndex);
    if (!story) return;
    e.preventDefault();
    this.openRead(story);
  };

  // Full-content reading modal (90%-opaque overlay over the media, all breakpoints).
  StoriesCampaign.prototype.openRead = function (story) {
    if (!this.readModal || !story) return;
    if (this._readHideTimer) { clearTimeout(this._readHideTimer); this._readHideTimer = null; }
    var metaLine = story.author || '';
    if (story.context) metaLine += (metaLine ? ' · ' : '') + story.context;
    this.readBody.innerHTML =
      (story.categoryName
        ? '<span class="stories__slide-badge" style="--cat-color:' + escAttr(story.categoryColor) +
            ';--cat-text:' + escAttr(story.categoryTextColor) + '">' + escHTML(story.categoryName) + '</span>'
        : '') +
      '<h3 class="stories__read-quote">' + escHTML(story.quote) + '</h3>' +
      (metaLine ? '<p class="stories__read-meta">' + escHTML(metaLine) + '</p>' : '') +
      (story.bodyHTML ? '<div class="stories__read-copy">' + story.bodyHTML + '</div>' : '');
    this.readBody.scrollTop = 0;
    this.readModal.hidden = false;
    this.readOpen = true;
    var m = this.readModal;
    if (this.reduced) { m.classList.add('is-open'); }
    else { requestAnimationFrame(function () { requestAnimationFrame(function () { m.classList.add('is-open'); }); }); }
    pushDL('stories_read_open', { story_id: story.id });
  };

  StoriesCampaign.prototype.closeRead = function () {
    if (!this.readModal || !this.readOpen) return;
    this.readModal.classList.remove('is-open');
    this.readOpen = false;
    var m = this.readModal, panel = this.readPanel, self = this;
    var hide = function () { self._readHideTimer = null; if (!m.classList.contains('is-open')) m.hidden = true; };
    if (this._readHideTimer) { clearTimeout(this._readHideTimer); this._readHideTimer = null; }
    if (this.reduced || !panel) { hide(); return; }
    var onEnd = function (e) {
      if (e.target !== panel || (e.propertyName !== 'transform' && e.propertyName !== 'opacity')) return;
      panel.removeEventListener('transitionend', onEnd);
      if (self._readHideTimer) clearTimeout(self._readHideTimer);
      hide();
    };
    panel.addEventListener('transitionend', onEnd);
    this._readHideTimer = setTimeout(function () { panel.removeEventListener('transitionend', onEnd); hide(); }, 360);
  };

  StoriesCampaign.prototype.toast = function (msg) {
    if (!this.player) return;
    var el = this._toastEl;
    if (!el) { el = this._toastEl = document.createElement('div'); el.className = 'stories__toast'; this.player.appendChild(el); }
    el.textContent = msg;
    el.classList.add('is-show');
    var self = this;
    if (this._toastTimer) clearTimeout(this._toastTimer);
    this._toastTimer = setTimeout(function () { el.classList.remove('is-show'); self._toastTimer = null; }, 1600);
  };

  StoriesCampaign.prototype.pauseAllVideos = function (keepMuteState) {
    this.slides.forEach(function (s) {
      var v = s._video;
      if (v && !keepMuteState) { try { v.pause(); } catch (e) { /* ignore */ } }
    });
    if (!keepMuteState) this._activeVideo = null;
  };

  /* ------------------------------------------------------- topic sheet */

  StoriesCampaign.prototype.openSheet = function () {
    if (!this.sheet) return;
    if (this._sheetHideTimer) { clearTimeout(this._sheetHideTimer); this._sheetHideTimer = null; }
    this.sheet.hidden = false;
    this.sheet.setAttribute('aria-hidden', 'false');
    // Add .is-open on the next frame so the slide-up transition runs from the
    // off-screen (translateY(100%)) start rather than snapping open.
    var sheet = this.sheet;
    if (this.reduced) {
      sheet.classList.add('is-open');
    } else {
      requestAnimationFrame(function () {
        requestAnimationFrame(function () { sheet.classList.add('is-open'); });
      });
    }
    this.sheetOpen = true;
    // Behind the 97%-opaque sheet, strip the post's text/UI so only the media
    // keeps playing in the background (restored on close).
    if (this.player) this.player.classList.add('is-textless');
    lockScroll();
    // Start the pending selection from the currently applied filter.
    this.sheetSel = this.filter.slice();
    this.syncSheetPills();
    this.updateSheetCount();
    this.updateSheetTabs();
    pushDL('stories_change_topic_open', {});
  };

  StoriesCampaign.prototype.closeSheet = function () {
    if (!this.sheetOpen) return;
    this.sheet.setAttribute('aria-hidden', 'true');
    this.sheet.classList.remove('is-open');   // triggers the slide-down transition
    this.sheetOpen = false;
    if (this.player) this.player.classList.remove('is-textless'); // bring the text back
    unlockScroll();

    // Keep the panel displayed until the slide-down finishes, then hide it.
    // Guarded so a fast reopen (is-open re-added) never gets force-hidden.
    var sheet = this.sheet;
    var panel = this.sheetPanel;
    var self = this;
    var hide = function () {
      self._sheetHideTimer = null;
      if (!sheet.classList.contains('is-open')) sheet.hidden = true;
    };
    if (this._sheetHideTimer) { clearTimeout(this._sheetHideTimer); this._sheetHideTimer = null; }
    if (this.reduced || !panel) {
      hide();
    } else {
      var onEnd = function (e) {
        if (e.target !== panel || (e.propertyName !== 'transform' && e.propertyName !== 'opacity')) return;
        panel.removeEventListener('transitionend', onEnd);
        if (self._sheetHideTimer) { clearTimeout(self._sheetHideTimer); }
        hide();
      };
      panel.addEventListener('transitionend', onEnd);
      this._sheetHideTimer = setTimeout(function () {   // fallback if transitionend never fires
        panel.removeEventListener('transitionend', onEnd);
        hide();
      }, 420);
    }
    // Filter application is explicit now (the "Filter" button), so closing the
    // sheet by itself (BACK / scrim) does NOT apply or fire an event.
  };

  // Toggle a pill in the PENDING selection only. Nothing is filtered yet — the
  // lit-up "Filter" button tells the user to press it to apply.
  StoriesCampaign.prototype.onSheetPill = function (pill) {
    var cat = pill.getAttribute('data-category');
    if (!cat) return;
    var pos = this.sheetSel.indexOf(cat);
    if (pos === -1) this.sheetSel.push(cat); else this.sheetSel.splice(pos, 1);
    this.syncSheetPills();
    this.updateSheetCount();
    this.updateSheetTabs();
  };

  // "See all" — clear any filter, close the sheet, and drop the viewer onto a
  // RANDOM story they can scroll from.
  StoriesCampaign.prototype.seeAll = function () {
    this.sheetSel = [];
    this.setFilter([], { fireApply: true });
    this.closeSheet();
    this.gotoRandom('see_all');
  };

  // "Filter" — apply the pending selection, close, and start the filtered feed.
  StoriesCampaign.prototype.applySheetFilter = function () {
    var sel = this.sheetSel.slice();
    if (!sel.length) { this.seeAll(); return; } // nothing picked → behave like See all
    this.setFilter(sel, { fireApply: true });
    this.closeSheet();
    this.gotoRandom('filter');
  };

  // Open (or jump) the player to a random story in the current set.
  StoriesCampaign.prototype.gotoRandom = function (source) {
    if (!this.set.length) return;
    var idx = Math.floor(Math.random() * this.set.length);
    pushDL('stories_random_open', { source: source || 'random', position: idx + 1 });
    if (this.playerOpen) {
      var slideIndex = this.slideIndexForStory(this.set[idx]);
      this.goToIndex(slideIndex, true);
    } else {
      this.open(idx, source || 'random');
    }
  };

  /* ----------------------------------------------------- static events */

  StoriesCampaign.prototype.bindStaticEvents = function () {
    var self = this;

    // Desktop grid category filter pills.
    this.filterPills.forEach(function (pill) {
      pill.addEventListener('click', function () {
        var cat = pill.getAttribute('data-category');
        self.setFilter(cat === 'all' ? [] : [cat], { fireApply: true });
      });
    });

    // Grid tile -> open player at that story within the current filtered set.
    // Exception: on desktop, a live preview video tile (poster-less, autoplaying
    // muted) toggles its own sound on click instead of opening the player.
    this.model.forEach(function (story, i) {
      story.el.addEventListener('click', function () {
        var gridVideo = story.el.querySelector('.stories__card-video');
        if (gridVideo && device() === 'desktop') {
          var willUnmute = gridVideo.muted; // currently muted → this click unmutes
          // one audio at a time: mute every grid preview first
          Array.prototype.forEach.call(
            self.grid.querySelectorAll('.stories__card-video'),
            function (o) { o.muted = true; }
          );
          gridVideo.muted = !willUnmute; // toggle
          if (willUnmute) safePlay(gridVideo);
          story.el.classList.toggle('is-unmuted', !gridVideo.muted);
          pushDL('stories_sound_toggle', { muted: gridVideo.muted, context: 'grid', story_id: story.id });
          return;
        }
        // Rebuild the current set (respecting active filter) and find index.
        self.set = self.storiesFor(self.filter);
        var idx = self.set.indexOf(story);
        if (idx === -1) idx = 0;
        self.open(idx, 'grid');
      });
    });

    if (this.playerClose) {
      this.playerClose.addEventListener('click', function () { self.closePlayer(); });
    }
    if (this.changeTopicBtn) {
      this.changeTopicBtn.addEventListener('click', function () { self.openSheet(); });
    }

    // Sheet interactions. "See all" = reset + random; "Filter" = apply pending.
    this.sheetTabs.forEach(function (btn) {
      btn.addEventListener('click', function () {
        if (btn.getAttribute('data-sheet-tab') === 'filter') self.applySheetFilter();
        else self.seeAll();
      });
    });
    this.sheetPills.forEach(function (pill) {
      pill.addEventListener('click', function () { self.onSheetPill(pill); });
    });
    if (this.sheetClear) {
      this.sheetClear.addEventListener('click', function () {
        self.sheetSel = []; // clear the PENDING selection (apply still needs "Filter")
        self.syncSheetPills();
        self.updateSheetCount();
        self.updateSheetTabs();
        pushDL('stories_clear_filters', {});
      });
    }
    this.sheetCloseEls.forEach(function (el) {
      el.addEventListener('click', function () { self.closeSheet(); });
    });
  };

  // Grid preview videos (video stories with no poster): keep muted, and only
  // play the ones on screen so a wall of tiles doesn't tank performance.
  // Reorder the grid DOM so media tiles are spread evenly among the (small) text
  // tiles — no two media end up next to each other, which gives the Pinterest
  // look. Only the wall's visual order changes; the player feed (this.model)
  // keeps its sort order since it holds element references, not positions.
  StoriesCampaign.prototype.reorderGridForMasonry = function () {
    if (!this.grid) return;
    var cards = Array.prototype.slice.call(this.grid.querySelectorAll('article[data-story]'));
    if (cards.length < 3) return;
    var media = [], text = [];
    cards.forEach(function (el) {
      (el.classList.contains('stories__card--media') ? media : text).push(el);
    });
    if (!media.length || !text.length) return;

    var total = cards.length;
    var slots = new Array(total);
    var step = total / media.length; // even spacing between media tiles
    for (var k = 0; k < media.length; k++) {
      var pos = Math.floor(k * step + step / 2);
      if (pos > total - 1) pos = total - 1;
      // nearest free slot; skip a spot adjacent to another media where possible
      var guard = 0;
      while (slots[pos] || (media.length * 2 <= total && (slots[pos - 1] || slots[pos + 1]))) {
        pos = (pos + 1) % total;
        if (++guard > total) break;
      }
      while (slots[pos]) { pos = (pos + 1) % total; }
      slots[pos] = media[k];
    }
    var ti = 0;
    for (var i = 0; i < total; i++) { if (!slots[i]) slots[i] = text[ti++]; }

    var frag = document.createDocumentFragment();
    slots.forEach(function (el) { if (el) frag.appendChild(el); });
    this.grid.appendChild(frag);
  };

  // Run the grid's one-off DOM work the first time the wall is on screen, and
  // again never. Below 768px that is usually never.
  StoriesCampaign.prototype.setupGridWhenVisible = function () {
    if (this._gridReady || !this.grid) return;
    var self = this;
    var gridShown = function () {
      return !!(self.grid.offsetWidth || self.grid.offsetHeight || self.grid.getClientRects().length);
    };
    var run = function () {
      if (self._gridReady || !gridShown()) return false;
      self._gridReady = true;
      self.reorderGridForMasonry(); // spread media so no two sit next to each other
      self.initGridVideos();
      self.tagGridOrientation();
      return true;
    };
    if (run()) return;
    // Not visible yet (mobile): retry once the viewport crosses the breakpoint.
    this._gridMQ = window.matchMedia ? window.matchMedia('(min-width: 768px)') : null;
    if (!this._gridMQ) return;
    this._onGridMQ = function () { run(); };
    if (this._gridMQ.addEventListener) this._gridMQ.addEventListener('change', this._onGridMQ);
    else if (this._gridMQ.addListener) this._gridMQ.addListener(this._onGridMQ);
  };

  StoriesCampaign.prototype.initGridVideos = function () {
    var vids = Array.prototype.slice.call(this.grid.querySelectorAll('.stories__card-video'));
    if (!vids.length) return;
    vids.forEach(function (v) { v.muted = true; });
    if (this.reduced) return; // reduced motion: leave paused on the first frame
    var self = this;
    if ('IntersectionObserver' in window) {
      this._gridVideoIO = new IntersectionObserver(function (entries) {
        entries.forEach(function (e) {
          if (e.isIntersecting) safePlay(e.target);
          else { try { e.target.pause(); } catch (x) {} }
        });
      }, { threshold: 0.25 });
      vids.forEach(function (v) { self._gridVideoIO.observe(v); });
    } else {
      vids.forEach(function (v) { safePlay(v); });
    }
  };

  // Tag every grid media tile (photo or video) with its orientation so the tile
  // sizes to 9:16 or 16:9. Server-known tiles already carry the class; this only
  // fills in unknowns (e.g. external video URLs) via measurement.
  StoriesCampaign.prototype.tagGridOrientation = function () {
    var cards = Array.prototype.slice.call(this.grid.querySelectorAll('.stories__card--media'));
    cards.forEach(function (card) {
      var media = card.querySelector('video, img');
      if (!media) return;
      var known = card.classList.contains('is-landscape') ? 'landscape'
                : card.classList.contains('is-portrait') ? 'portrait' : null;
      applyMediaOrientation(media, card, known);
    });
  };

  StoriesCampaign.prototype.onKeydown = function (e) {
    if (this.readOpen) {
      if (e.key === 'Escape') { e.preventDefault(); this.closeRead(); }
      return;
    }
    if (this.moreMenuOpen) {
      if (e.key === 'Escape') { e.preventDefault(); this.closeMoreMenu(); }
      return;
    }
    if (this.sheetOpen) {
      if (e.key === 'Escape') { e.preventDefault(); this.closeSheet(); }
      return;
    }
    if (!this.playerOpen) return;
    switch (e.key) {
      case 'Escape': e.preventDefault(); this.closePlayer(); break;
      case 'ArrowDown':
      case 'ArrowRight': e.preventDefault(); this.next(); break;
      case 'ArrowUp':
      case 'ArrowLeft': e.preventDefault(); this.prev(); break;
      default: break;
    }
  };

  StoriesCampaign.prototype.onResize = function () {
    // Keep the active slide aligned after a viewport/orientation change.
    if (!this.playerOpen) return;
    this.syncCloseBtn();
    this.track.scrollTop = clamp(this.activeIndex, 0, this.slideModel.length - 1) * this.slideH();
  };

  // TikTok-style mobile feed has no exit chrome — the close button is desktop-only.
  // Driven off device() (same 1024px break as the player) so it never depends on
  // a CSS media query matching the preview/iframe width.
  StoriesCampaign.prototype.syncCloseBtn = function () {
    if (this.playerClose) this.playerClose.style.display = device() === 'mobile' ? 'none' : '';
  };

  /* ---------------------------------------------------------- teardown */

  StoriesCampaign.prototype.destroy = function () {
    this.clearAdvance();
    document.removeEventListener('keydown', this._onKeydown);
    window.removeEventListener('resize', this._onResize);
    if (this.track) this.track.removeEventListener('scroll', this._onScroll);
    if (this.track && this._onTextLike) this.track.removeEventListener('click', this._onTextLike);
    if (this.track && this._onBodyTap) this.track.removeEventListener('click', this._onBodyTap);
    if (this.track && this._onReadMore) this.track.removeEventListener('click', this._onReadMore);
    if (this.track && this._onTrackTouchStart) {
      this.track.removeEventListener('touchstart', this._onTrackTouchStart);
      this.track.removeEventListener('touchmove', this._onTrackTouchMove);
      this.track.removeEventListener('touchend', this._onTrackTouchEnd);
      this.track.removeEventListener('wheel', this._onTrackWheel);
    }
    if (this._gridVideoIO) { this._gridVideoIO.disconnect(); this._gridVideoIO = null; }
    if (this._gridMQ && this._onGridMQ) {
      if (this._gridMQ.removeEventListener) this._gridMQ.removeEventListener('change', this._onGridMQ);
      else if (this._gridMQ.removeListener) this._gridMQ.removeListener(this._onGridMQ);
      this._gridMQ = null;
    }
    if (this.playerOpen) unlockScroll();
    if (this.sheetOpen) unlockScroll();
    if (this.root) this.root.__storiesInstance = null;
  };

  /* ------------------------------------------------- excerpt / read more */
  var READ_MORE_LABEL = 'Read more';
  var EXCERPT_MAX = 150;

  // First ~150 characters of the body as plain text, cut on a word boundary.
  // The slide only teases the story; the full copy lives in the read modal.
  function excerptFrom(str, max) {
    max = max || EXCERPT_MAX;
    var text = String(str == null ? '' : str).replace(/\s+/g, ' ').trim();
    if (text.length <= max) return text;
    var cut = text.slice(0, max);
    var space = cut.lastIndexOf(' ');
    if (space > max * 0.6) cut = cut.slice(0, space);
    return cut.replace(/[\s,;:.!?-]+$/, '') + '…';
  }

  /* ----------------------------------------------------- small escapes */
  function escHTML(str) {
    return String(str == null ? '' : str)
      .replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');
  }
  function escAttr(str) {
    return escHTML(str).replace(/"/g, '&quot;');
  }

  /* --------------------------------------------------------------- boot */

  function init(root) {
    if (!root || root.__storiesInstance) return;
    try {
      new StoriesCampaign(root);
    } catch (e) {
      if (window.console && console.error) console.error('[stories-campaign] init failed', e);
    }
  }

  function boot() {
    var roots = document.querySelectorAll('[data-stories-root]');
    for (var i = 0; i < roots.length; i++) init(roots[i]);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot(); // `defer` guarantees the DOM is parsed
  }

  // Theme editor: (re)initialise when this section is loaded/reloaded.
  document.addEventListener('shopify:section:load', function (e) {
    if (!e.target || !e.target.querySelector) return;
    var root = e.target.matches && e.target.matches('[data-stories-root]')
      ? e.target
      : e.target.querySelector('[data-stories-root]');
    if (!root) return;
    if (root.__storiesInstance && root.__storiesInstance.destroy) root.__storiesInstance.destroy();
    init(root);
  });

  document.addEventListener('shopify:section:unload', function (e) {
    if (!e.target || !e.target.querySelector) return;
    var root = e.target.querySelector ? e.target.querySelector('[data-stories-root]') : null;
    if (root && root.__storiesInstance && root.__storiesInstance.destroy) {
      root.__storiesInstance.destroy();
    }
  });
})();
